import {adminApiUrl, publicApiUrl, signedInApiUrl} from "./Constants";
import axios from "axios";
import { useSyncExternalStore } from "react";
import type { User } from "../types/domain";

// Auth state lives in a module-level store with a subscribe/notify pattern so React components
// can react to its changes via the `useAuth` / `useLoggedUser` hooks. Synchronous accessors
// (`getLoggedUser`, `isAdmin`, `isSuperAdmin`) remain for non-React call sites (API helpers, etc.).
interface AuthState {
  user: User | null;
  // `false` until validateToken() resolves at least once; lets gated pages distinguish
  // "still loading" from "definitely not logged in".
  validated: boolean;
}

let authState: AuthState = { user: null, validated: false };
const listeners = new Set<() => void>();

const setAuthState = (next: Partial<AuthState>): void => {
  authState = { ...authState, ...next };
  listeners.forEach((l) => l());
};

const subscribe = (callback: () => void): (() => void) => {
  listeners.add(callback);
  return () => { listeners.delete(callback); };
};

const getSnapshot = (): AuthState => authState;

export const useAuth = (): AuthState => useSyncExternalStore(subscribe, getSnapshot);

// SUPERADMIN inherits ADMIN privileges (mirrors backend role hierarchy), so this returns true
// for both. Use `isSuperAdmin()` when an action should be exclusive to SUPERADMIN.
export const isAdmin = (): boolean => {
  return authState.user?.role === "ADMIN" || authState.user?.role === "SUPERADMIN"
}

export const isSuperAdmin = (): boolean => {
  return authState.user?.role === "SUPERADMIN"
}

export const getLoggedUser = (): User | null => {
  return authState.user
}

export const getToken = (): string | null => {
  return localStorage.getItem("jwt-auth-token")
}

export const logout = (): void => {
  localStorage.removeItem("jwt-auth-token")
  setAuthState({ user: null })
}

export const getLanguage = (): string => {
  const language = localStorage.getItem("language")
  if (language) {
    axios.defaults.headers.common["Accept-Language"] = language
    return language
  } else {
    return "cs" // Default language
  }
}

export const setLanguage = (language: string): void => {
  localStorage.setItem("language", language)
  axios.defaults.headers.common["Accept-Language"] = language
}

export const login = async (username: string, password: string): Promise<boolean> => {
  return await axios.get(`${publicApiUrl}/token?username=${username}&password=${password}`).then(response => {
    if (response.status === 200 && response.data) {
      localStorage.setItem("jwt-auth-token", response.data)
      return true
    } else {
      return false
    }
  })
}

export const changePassword = async (currentPassword: string, newPassword: string): Promise<boolean> => {
  const body = {currentPassword, newPassword}
  return await axios.put(`${signedInApiUrl}/users/changePassword`, body).then(response => {
    return response.status === 200;
  })
}

export const resetPassword = async (userId: number): Promise<boolean> => {
  return await axios.put(`${adminApiUrl}/users/${userId}/resetPassword`).then(response => {
    return response.status === 200;
  })
}

export const validateToken = async (): Promise<void> => {
  const jwtToken = localStorage.getItem("jwt-auth-token")
  if (jwtToken == null || jwtToken === "null") {
    setAuthState({ user: null, validated: true })
    return
  }
  try {
    const response = await axios.get<User>(`${publicApiUrl}/token/validate?token=${jwtToken}`)
    if (response.status === 200) {
      axios.defaults.headers.common["Authorization"] = "Bearer " + jwtToken
      setAuthState({ user: response.data, validated: true })
    } else {
      localStorage.removeItem("jwt-auth-token")
      setAuthState({ user: null, validated: true })
    }
  } catch {
    localStorage.removeItem("jwt-auth-token")
    setAuthState({ user: null, validated: true })
  }
}

export const setAuthorizationHeader = (): void => {
  const jwtToken = localStorage.getItem("jwt-auth-token")
  if (jwtToken != null && jwtToken !== "null") {
    axios.defaults.headers.common["Authorization"] = "Bearer " + jwtToken;
  } else {
    delete axios.defaults.headers.common["Authorization"];
  }
}
